import { DocumentRecord } from "@/hooks/useDocuments";
import { FileText, DollarSign, Banknote, Weight, Box, Package, Filter, MousePointerClick } from "lucide-react";
import { cn } from "@/lib/utils";

type KPIMode = "global" | "filtered" | "selection";

interface KPICardsProps {
  documents: DocumentRecord[];
  documentType?: string;
  mode?: KPIMode;
}

function ModeIndicator({ mode }: { mode: KPIMode }) {
  if (mode === "global") return null;
  const isSelection = mode === "selection";
  return (
    <div className={cn(
      "flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full w-fit",
      isSelection
        ? "bg-primary/10 text-primary"
        : "bg-accent/50 text-accent-foreground"
    )}>
      {isSelection ? <MousePointerClick className="h-3 w-3" /> : <Filter className="h-3 w-3" />}
      {isSelection ? "Mostrando selección" : "Datos filtrados"}
    </div>
  );
}

function KPICard({ icon: Icon, label, value, mode, iconClassName }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  mode: KPIMode;
  iconClassName?: string;
}) {
  return (
    <div className={cn(
      "glass rounded-xl p-5 glow-cyan transition-all hover:glow-cyan-strong",
      mode === "selection" && "ring-1 ring-primary/30",
      mode === "filtered" && "ring-1 ring-accent/30"
    )}>
      <div className="flex items-center gap-3 mb-1">
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", iconClassName || "bg-primary/10")}>
          <Icon className={cn("h-4 w-4", iconClassName ? "text-secondary" : "text-primary")} />
        </div>
        <span className="text-sm text-muted-foreground font-medium">{label}</span>
      </div>
      <ModeIndicator mode={mode} />
      <p className="text-3xl font-bold text-foreground mt-2 tabular-nums tracking-tight">{value}</p>
    </div>
  );
}

export function KPICards({ documents, documentType, mode = "global" }: KPICardsProps) {
  const isPacking = documentType?.toLowerCase() === "packing list";

  const totalUSD = documents
    .filter(d => (d.moneda || "").toUpperCase() === "USD")
    .reduce((sum, d) => sum + Number(d.total_factura), 0);
  const totalCOP = documents
    .filter(d => (d.moneda || "").toUpperCase() === "COP")
    .reduce((sum, d) => sum + Number(d.total_factura), 0);

  const totalPeso = documents.reduce((sum, d) => sum + Number(d.peso_bruto_kg || 0), 0);
  const totalVolumen = documents.reduce((sum, d) => sum + Number(d.volumen_cbm || 0), 0);
  const totalPiezas = documents.reduce((sum, d) => sum + Number(d.total_piezas || 0), 0);

  if (isPacking) {
    return (
      <div className="grid gap-4 grid-cols-1 md:grid-cols-4">
        <KPICard icon={FileText} label="Total Registros" value={documents.length} mode={mode} />
        <KPICard icon={Weight} label="Peso Bruto (KG)" value={totalPeso.toLocaleString("en-US", { minimumFractionDigits: 2 })} mode={mode} />
        <KPICard icon={Box} label="Volumen (CBM)" value={totalVolumen.toLocaleString("en-US", { minimumFractionDigits: 2 })} mode={mode} />
        <KPICard icon={Package} label="Total Piezas" value={totalPiezas.toLocaleString("en-US")} mode={mode} />
      </div>
    );
  }

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
      <KPICard icon={FileText} label="Total Registros" value={documents.length} mode={mode} />
      <KPICard icon={DollarSign} label="Total USD" value={`$${totalUSD.toLocaleString("en-US", { minimumFractionDigits: 2 })}`} mode={mode} />
      <KPICard icon={Banknote} label="Total COP" value={`$${totalCOP.toLocaleString("es-CO", { minimumFractionDigits: 2 })}`} mode={mode} iconClassName="bg-secondary/10" />
    </div>
  );
}
